import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import * as XLSX from "xlsx";
import useApi from "../../../CustomHooks/useApi";
import { 
  Search, 
  FileSpreadsheet, 
  Printer, 
  Calendar, 
  Filter, 
  Building2, 
  CheckCircle2, 
  XCircle, 
  Clock,
  RotateCw,
  ChevronLeft,
  ChevronRight
} from "lucide-react";
import { getDisplayDate } from "../../../ResuableCompoment/GetDisplayDate";
import { openPrint } from "../../../Store/Slices/printSlice";
import PrintPopup from "../../../ResuableCompoment/PrintPopup";
import DynamicTable from "../../../ResuableCompoment/DynamicTable";

const AepsHistory = () => {
  const mid = useSelector((state) => state.auth.employeeDetails?.username);
  const dispatch = useDispatch();

  const { callApi: AespTxn } = useApi("/aeps/transaction/history", "GET");

  const getToday = () => new Date().toISOString().split("T")[0];

  const [fromDate, setFromDate] = useState(getToday());
  const [toDate, setToDate] = useState(getToday());

  /* ================= STATES ================= */
  const [history, setHistory] = useState([]);
  const [isHistoryLoading, setIsHistoryLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const recordsPerPage = 25;
  const [searchTerm, setSearchTerm] = useState("");

  /* ================= TABLE COLUMNS ================= */
  const columns = [
    {
      key: "date",
      label: "Date",
      render: (v) => getDisplayDate(v),
    },
    {
      key: "txnId",
      label: "Txn ID",
      render: (v) => (
        <span className="font-mono text-slate-700 bg-slate-100/70 px-2 py-0.5 rounded-md border border-slate-200/50">
          {v || "—"}
        </span>
      ),
    },
    {
      key: "aeps_txn_mode",
      label: "Mode",
      render: (v) => (
        <span className="px-2 py-0.5 rounded-md text-[11px] font-bold bg-slate-100 text-slate-700 uppercase">
          {v || "—"}
        </span>
      ),
    },
    {
      key: "aeps_AadhaarNo",
      label: "Aadhaar",
      render: (v) => (
        <span className="font-mono text-slate-500">
          {v || "—"}
        </span>
      ),
    },
    {
      key: "bankRrn",
      label: "Bank RRN",
      render: (v) => (
        <span className="font-mono text-slate-500">
          {v || "—"}
        </span>
      ),
    },
    {
      key: "beneficiaryMobileNumber",
      label: "Mobile",
      render: (v) => (
        <span className="text-slate-700 font-medium">
          {v || "—"}
        </span>
      ),
    },
    {
      key: "beneBankName",
      label: "Bank",
      render: (v) => (
        <span className="font-semibold text-slate-800">
          {v || "—"}
        </span>
      ),
    },
    {
      key: "txnAmount",
      label: "Amount",
      render: (v) => (
        <span className="font-bold text-slate-900 text-sm">
          ₹{Number(v || 0).toFixed(2)}
        </span>
      ),
    },
    {
      key: "description",
      label: "Reason",
      render: (v) => (
        <div className="max-w-xs whitespace-normal break-words leading-relaxed text-slate-600 text-xs">
          {v || "—"}
        </div>
      ),
    },
    {
      key: "txnStatus",
      label: "Status",
      render: (v) => {
        const status = (v || "").toUpperCase();
        if (status === "SUCCESS") {
          return (
            <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
              <CheckCircle2 className="w-3 h-3" /> SUCCESS
            </span>
          );
        }
        if (status === "FAILED") {
          return (
            <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-semibold bg-rose-50 text-rose-700 border border-rose-200">
              <XCircle className="w-3 h-3" /> FAILED
            </span>
          );
        }
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-semibold bg-amber-50 text-amber-700 border border-amber-200">
            <Clock className="w-3 h-3" /> {status || "PENDING"}
          </span>
        );
      },
    },
    {
      key: "action",
      label: "Receipt",
      render: (_, row) => (
        <button
          onClick={() => dispatch(openPrint(row))}
          className="p-1.5 rounded-xl border border-slate-200 text-slate-600 hover:text-blue-600 hover:border-blue-200 bg-white hover:bg-slate-50 transition active:scale-95 inline-flex items-center gap-1 shadow-2xs"
          title="Print Receipt"
        >
          <Printer className="w-3.5 h-3.5" />
          <span className="text-[11px] font-semibold">Print</span>
        </button>
      ),
    },
  ];

  /* ================= FETCH HISTORY ================= */
  const fetchAepsHistory = async () => {
    setIsHistoryLoading(true);
    try {
      let query = `?merchant_user_name=${mid}`;

      if (fromDate) query += `&fromDate=${fromDate}`;
      if (toDate) query += `&toDate=${toDate}`;

      const res = await AespTxn(null, query);
      const sorted = (res || []).sort(
        (a, b) => new Date(b.date) - new Date(a.date)
      );
      setHistory(sorted);
      setCurrentPage(1);
    } catch (err) {
      console.error(err);
      // alert("Failed to load AEPS history");
      console.log("Failed to load AEPS history");
    } finally {
      setIsHistoryLoading(false);
    }
  };

  useEffect(() => {
    fetchAepsHistory();
    // eslint-disable-next-line
  }, []);

  /* ================= SEARCH + PAGINATION ================= */
  const filteredRecords = history.filter((record) =>
    Object.values(record).some((value) =>
      String(value).toLowerCase().includes(searchTerm.toLowerCase())
    )
  );

  const totalPages = Math.ceil(filteredRecords.length / recordsPerPage) || 1;
  const indexOfLastRecord = currentPage * recordsPerPage;
  const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
  const currentRecords = filteredRecords.slice(
    indexOfFirstRecord,
    indexOfLastRecord
  );

  /* ================= EXCEL DOWNLOAD ================= */
  const downloadExcel = () => {
    const rows = history.map((r) => ({
      Date: r.date,
      TxnID: r.txnId,
      Aadhaar: r.aeps_AadhaarNo,
      Bank: r.beneBankName,
      Mobile: r.beneficiaryMobileNumber,
      Amount: r.txnAmount,
      Status: r.txnStatus,
      Reason: r.description,
    }));

    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "AEPS History");
    XLSX.writeFile(wb, "AEPS_Transaction_History.xlsx");
  };

  return (
    <div className="space-y-6">
      {/* ================= SECTION HEADER ================= */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-blue-50 text-blue-700 border border-blue-100 mb-2">
            <Building2 className="w-3.5 h-3.5" />
            AEPS Settlement & Transaction Logs
          </div>
          <h2 className="text-xl sm:text-2xl font-bold text-slate-800 tracking-tight">
            AEPS History
          </h2>
          <p className="text-xs text-slate-500 font-medium mt-0.5">
            Real-time logs for Cash Withdrawal, Mini Statements, and Balance Inquiries
          </p>
        </div>
      </div>

      {/* ================= FILTER & SEARCH BAR ================= */}
      <div className="bg-white rounded-3xl p-5 border border-slate-200/80 shadow-xs space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-4">
          
          {/* Search Box */}
          <div className="relative w-full sm:w-80">
            <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              placeholder="Search Txn ID, Mobile, Bank..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 bg-slate-50 border border-slate-200/80 focus:border-blue-500 focus:bg-white rounded-xl text-xs font-semibold text-slate-700 placeholder-slate-400 outline-hidden transition"
            />
          </div>

          {/* Date Picker & Action Buttons */}
          <div className="flex flex-wrap items-center gap-2.5 w-full sm:w-auto">
            <div className="flex items-center gap-2">
              <input
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                className="h-10 px-3 bg-slate-50 border border-slate-200/80 focus:border-blue-500 focus:bg-white rounded-xl text-xs font-semibold text-slate-700 outline-hidden transition"
              />
              <span className="text-slate-400 text-xs font-bold">to</span>
              <input
                type="date"
                value={toDate}
                min={fromDate}
                onChange={(e) => setToDate(e.target.value)}
                className="h-10 px-3 bg-slate-50 border border-slate-200/80 focus:border-blue-500 focus:bg-white rounded-xl text-xs font-semibold text-slate-700 outline-hidden transition"
              />
            </div>

            <button
              onClick={fetchAepsHistory}
              disabled={isHistoryLoading}
              className="h-10 px-4 rounded-xl bg-slate-900 hover:bg-slate-800 text-white font-bold text-xs shadow-md shadow-slate-900/10 transition active:scale-95 flex items-center gap-1.5"
            >
              <RotateCw className={`w-3.5 h-3.5 ${isHistoryLoading ? "animate-spin" : ""}`} />
              <span>Filter</span>
            </button>

            <button
              onClick={downloadExcel}
              className="h-10 px-4 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs shadow-md shadow-emerald-600/15 transition active:scale-95 flex items-center gap-1.5"
            >
              <FileSpreadsheet className="w-3.5 h-3.5" />
              <span>Excel</span>
            </button>
          </div>
        </div>

        {/* Count Preview */}
        <div className="pt-2 border-t border-slate-100 flex items-center justify-between text-xs text-slate-500 font-medium">
          <span>
            Showing <strong className="text-slate-800">{currentRecords.length}</strong> of{" "}
            <strong className="text-slate-800">{filteredRecords.length}</strong> records
          </span>
          <span>
            Page <strong className="text-blue-600 font-bold">{currentPage}</strong> of{" "}
            <strong>{totalPages}</strong>
          </span>
        </div>
      </div>

      {/* ================= TABLE CONTAINER ================= */}
      <div className="bg-white rounded-3xl border border-slate-200/80 shadow-xs p-2 sm:p-4">
        <DynamicTable
          columns={columns}
          data={currentRecords}
          isLoading={isHistoryLoading}
          emptyText="No AEPS records found for the selected dates"
        />

        {/* ================= PAGINATION CONTROLS ================= */}
        {filteredRecords.length > recordsPerPage && (
          <div className="mt-4 pt-3 border-t border-slate-100 flex flex-wrap items-center justify-between gap-4 text-xs font-semibold text-slate-600 px-2">
            <span>
              Page {currentPage} of {totalPages}
            </span>

            <div className="flex items-center gap-2">
              <button
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((p) => p - 1)}
                className="px-3 py-1.5 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 disabled:opacity-40 transition active:scale-95 flex items-center gap-1"
              >
                <ChevronLeft className="w-3.5 h-3.5" />
                <span>Prev</span>
              </button>

              <button
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage((p) => p + 1)}
                className="px-3 py-1.5 rounded-xl bg-slate-900 hover:bg-slate-800 text-white disabled:opacity-40 transition active:scale-95 flex items-center gap-1"
              >
                <span>Next</span>
                <ChevronRight className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        )}
      </div>

      <PrintPopup />
    </div>
  );
};

export default AepsHistory;